/**
 * Zeffor: "Mehr laden"-Button statt Endlos-Scroll / Standard-Pagination.
 *
 * Bewusst als eigenstaendiges Custom Element ohne Theme-Imports gebaut, damit
 * es unabhaengig von Horizons interner PaginatedList-Logik funktioniert (deren
 * Render-Methoden sind private und von aussen nicht aufrufbar).
 *
 * Funktionsweise: liest data-last-page vom Grid und data-page von den Karten,
 * holt die naechste Seite per fetch und haengt deren Karten ans bestehende Grid.
 */
class ZefforLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-zf-loadmore-button]');
    this.counter = this.querySelector('[data-zf-loadmore-count]');
    if (!this.button) return;

    this.button.addEventListener('click', () => this.loadNext());

    this.sync();

    // Nach Filter-/Sortier-Wechsel wird das Grid ausgetauscht: Status neu bestimmen.
    document.addEventListener('shopify:section:load', () => this.sync());
    document.addEventListener('search:update', () => this.sync());
    document.addEventListener('collection:update', () => this.sync());
  }

  get grid() {
    return document.querySelector('[ref="grid"]');
  }

  get lastPage() {
    return Number(this.grid?.dataset.lastPage || 1);
  }

  get currentPage() {
    const grid = this.grid;
    if (!grid) return 1;
    let max = 1;
    grid.querySelectorAll('[data-page]').forEach((card) => {
      const page = Number(card.dataset.page);
      if (page > max) max = page;
    });
    return max;
  }

  sync() {
    const grid = this.grid;
    if (!grid || !this.button) return;

    const hasMore = this.currentPage < this.lastPage;
    this.hidden = !hasMore;
    this.button.hidden = !hasMore;

    if (this.counter) {
      const shown = grid.querySelectorAll('[data-page]').length;
      const total = Number(grid.dataset.totalCount || 0);
      this.counter.textContent = total > 0 ? shown + ' von ' + total : '';
      this.counter.hidden = total === 0;
    }
  }

  async loadNext() {
    const grid = this.grid;
    const next = this.currentPage + 1;
    if (!grid || next > this.lastPage) return;

    const label = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = this.dataset.loadingLabel || 'Lädt …';

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(next));
      url.hash = '';

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('HTTP ' + response.status);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = parsed.querySelector('[ref="grid"]');

      if (incoming) {
        const nodes = Array.from(incoming.children).map((node) => document.importNode(node, true));
        grid.append(...nodes);
        // URL mitziehen, damit Zurueck-Navigation und Teilen die Position behalten.
        history.replaceState('', '', url.toString());
      }
    } catch (error) {
      console.error('[zeffor-load-more]', error);
    } finally {
      this.button.disabled = false;
      this.button.textContent = label;
      this.sync();
    }
  }
}

if (!customElements.get('zeffor-load-more')) {
  customElements.define('zeffor-load-more', ZefforLoadMore);
}
